import $ from "jquery";
import { PMTiles } from "pmtiles";
import { MapView } from "./Basemap";
import { ForecastTime, InitialTime, ShadeSelector } from "./Controller";
import {
    CONTOUR_PMTILES_CUTOFF,
    QMA_CONTOUR_PMTILES_DIRECTORY_CUTOFF,
    beforeInitialCutoff,
} from "./DataAvailability";
import {
    ColorPicker,
    InputNumber,
    OnOffButton,
    Selectbox,
    setFeatureAvailability,
} from "./Form";
import { gslForecastKey, gslTargetKey, usesGslAnalysis } from "./GslTime";
import { getMapMeta } from "./MapMeta";
import { readFlag } from "./UrlState";
import { dataPath } from "./paths";

const SVG_NS = "http://www.w3.org/2000/svg";
const TILE_Z = 8;
const DEFAULT_FILLS = [
    "#000066cc",
    "#2255eecc",
    "#5555ff77",
    "#ccccff77",
    "#ffffff00",
    "#ffffff00",
];
const DEFAULT_LINES = Array(6).fill("#ffffffff");
const PALETTE = [
    "#ffffff00",
    "#ffffffff",
    "#6699ddff",
    "#eeff66ff",
    "#000066cc",
    "#2255eecc",
    "#5555ff77",
    "#ccccff77",
    "#ee2266cc",
    "#ff6633cc",
    "#ff9933cc",
    "#ffcc88cc",
];
const SUPPORTED_MODELS = new Set(["MSM", "GSM", "GSA", "GSL", "QMA"]);

type ContourVersion =
    | "T975"
    | "T925"
    | "T850"
    | "E850"
    | "T500"
    | "H500"
    | "H300"
    | "T300"
    | "T850A"
    | "H500A"
    | "T500A"
    | "H300A"
    | "T300A";
type WarmCold = "C" | "W" | "B";
interface ContourMetadata {
    format?: string;
    values?: number[];
    origin?: number[];
    full_svg_units?: number[];
    value_mapping?: string;
    value_step?: number;
}
interface Layer {
    value: number;
    path: Path2D;
    region: Path2D;
    style: { fill: Rgba; line: Rgba };
}
interface LayerSpec {
    value: number;
    side: "C" | "W";
    indices: number[];
}
interface Rgba {
    css: string;
    alpha: number;
}
interface Preset {
    fills: string[];
    lines?: string[];
    thresholds: number[];
    side: WarmCold;
}

export class Contour {
    private readonly group = document.querySelector<SVGGElement>("#contour")!;
    private readonly canvas = document.createElement("canvas");
    private readonly foreignObject = document.createElementNS(
        SVG_NS,
        "foreignObject",
    );
    private readonly legend = document.createElementNS(SVG_NS, "g");
    private readonly onOff = new OnOffButton("contour");
    private readonly version = new Selectbox("#select_Cver");
    private readonly side = new Selectbox("#select_Cwc");
    private readonly strokeWidth = new InputNumber("#input_Cstw", {
        value: 1.5,
        min: 0,
    });
    private readonly fillColors = DEFAULT_FILLS.map(
        (color, index) =>
            new ColorPicker(`#colpic_Cfill${index}`, color, PALETTE),
    );
    private readonly lineColors = DEFAULT_LINES.map(
        (color, index) =>
            new ColorPicker(`#colpic_Cline${index}`, color, PALETTE),
    );
    private readonly thresholdSelects = [...Array(6)].map(
        (_, index) =>
            document.querySelector<HTMLSelectElement>(`#select_Cfill${index}`)!,
    );
    private readonly archives = new Map<string, Promise<PMTiles | null>>();
    private readonly archiveOrder: string[] = [];
    private readonly paths = new Map<string, Promise<Path2D[]>>();
    private readonly pathOrder: string[] = [];
    private readonly legacyCache = new Map<
        string,
        Promise<Map<number, Path2D[]> | null>
    >();
    private readonly legacyOrder: string[] = [];
    private changeCallback: (task: Promise<unknown>) => void = () => {};
    private enabled = false;
    private archive: PMTiles | null = null;
    private legacyPaths: Map<number, Path2D[]> | null = null;
    private archiveUrl = "";
    private scope = "";
    private values: number[] = [];
    private valueStep = 1;
    private thresholds = Array(6).fill(-99999) as number[];
    private selectedLevels: number[] = [];
    private hitLayers: Layer[] = [];
    private sequence = 0;
    private drawSequence = 0;
    private prefetchPreviousFt: import("decimal.js").default | null = null;
    private captureWidth: number | null = null;
    private paramsExplicitStyle = false;
    private styleNeedsInitialization = true;
    private legendX = 50;
    private legendY = 350;

    constructor(
        private readonly params: URLSearchParams,
        private readonly shadeSelector: ShadeSelector,
        private readonly initialTime: InitialTime,
        private readonly forecastTime: ForecastTime,
        private readonly mapView: MapView,
    ) {
        this.canvas.id = "contour_tiles";
        this.canvas.style.display = "block";
        this.foreignObject.id = "contour_tiles_foreign_object";
        this.foreignObject.style.pointerEvents = "none";
        this.foreignObject.appendChild(this.canvas);
        this.group.insertBefore(this.foreignObject, this.group.firstChild);
        this.legend.id = "legendC";
        this.legend.style.pointerEvents = "auto";
        this.legend.style.cursor = "move";
        document.querySelector("#overlay")!.appendChild(this.legend);
        this.installLegendDrag();

        this.onOff.onChange(() => this.setEnabled(this.onOff.value === "1"));
        this.version.onChange(() => this.variableChanged());
        this.side.onChange(() => this.changed());
        this.strokeWidth.onChange(() => this.changed());
        this.thresholdSelects.forEach((select, index) =>
            select.addEventListener("change", () => {
                this.thresholds[index] = Number(select.value);
                this.changed();
            }),
        );
        this.fillColors.forEach((picker) =>
            picker.onChange(() => this.changed()),
        );
        this.lineColors.forEach((picker) =>
            picker.onChange(() => this.changed()),
        );
        for (let index = 0; index < 5; index++) {
            $(`#Cpreset${index + 4}`).on("click", (event) => {
                event.preventDefault();
                const name = String(
                    document
                        .querySelector(`#Cpreset${index + 4}`)
                        ?.getAttribute("data-preset") || "",
                );
                if (name) {
                    this.applyPreset(name);
                }
            });
        }
        const main = document.querySelector<SVGSVGElement>("#svg_main")!;
        main.addEventListener("mousemove", (event) => this.hover(event), true);
        main.addEventListener("mouseleave", () => $("#info").hide(), true);
        this.loadParams();
        this.updateModelOptions();
        this.updateUi();
    }

    onChange(callback: (task: Promise<unknown>) => void) {
        this.changeCallback = callback;
    }
    saveParams() {
        this.params.set("con_on", this.enabled ? "1" : "0");
        this.params.set("con_ver", this.version.value);
        this.params.set("con_stw", String(this.strokeWidth.value));
        this.params.set("con_wc", this.side.value);
        this.params.set(
            "con_fcols",
            this.fillColors.map((p) => p.getColor()).join(","),
        );
        this.params.set(
            "con_lcols",
            this.lineColors.map((p) => p.getColor()).join(","),
        );
        this.params.set("con_ths", this.thresholds.join(","));
        this.params.delete("con_lvsflg");
        this.params.set("lgdC_x", String(this.legendX));
        this.params.set("lgdC_y", String(this.legendY));
    }
    loadParams() {
        this.enabled = readFlag(this.params, "con_on", "0") === "1";
        this.onOff.value = this.enabled ? "1" : "0";
        this.version.value = this.validVersion(this.params.get("con_ver"));
        this.strokeWidth.value = this.numberParam("con_stw", 1.5, 0, 20);
        this.side.value = ["C", "W", "B"].includes(
            this.params.get("con_wc") || "",
        )
            ? this.params.get("con_wc")!
            : "C";
        this.legendX = this.numberParam("lgdC_x", 50, -1000, 3000);
        this.legendY = this.numberParam("lgdC_y", 350, -1000, 2000);
        this.paramsExplicitStyle = ["con_fcols", "con_lcols", "con_ths"].some(
            (key) => this.params.has(key),
        );
        DEFAULT_FILLS.forEach((color, index) =>
            this.fillColors[index].setColor(color),
        );
        DEFAULT_LINES.forEach((color, index) =>
            this.lineColors[index].setColor(color),
        );
        this.thresholds = Array(6).fill(-99999);
        this.loadColors("con_fcols", this.fillColors);
        this.loadColors("con_lcols", this.lineColors);
        const thresholds = this.params.get("con_ths")?.split(",").map(Number);
        if (thresholds?.length === 6 && thresholds.every(Number.isFinite)) {
            this.thresholds = thresholds;
        }
        this.styleNeedsInitialization = true;
        this.updateSelectedLevels();
        this.updateUi();
    }
    onShadeVariableChanged() {
        this.updateModelOptions();
        this.updateAvailability();
        return this.update(true);
    }
    onInitialTimeChanged() {
        return this.update(true);
    }
    onForecastTimeChanged() {
        return this.update(false);
    }
    onViewChanged() {
        return this.draw();
    }
    updateWidth() {
        return this.draw();
    }
    async toCaptureMode(width: number) {
        this.captureWidth = width;
        await this.draw();
    }
    async toDisplayMode() {
        this.captureWidth = null;
        await this.draw();
    }

    private setEnabled(enabled: boolean) {
        this.enabled = enabled;
        this.onOff.value = enabled ? "1" : "0";
        this.updateUi();
        this.changeCallback(this.update(false));
    }
    private variableChanged() {
        this.paramsExplicitStyle = false;
        this.changeCallback(this.update(true));
    }
    private changed() {
        this.updateSelectedLevels();
        this.updateUi();
        this.changeCallback(this.draw());
    }
    private async update(resetStyle: boolean) {
        const currentSequence = ++this.sequence;
        if (!this.enabled) {
            this.clear();
            return;
        }
        const model = this.shadeSelector.getParams().model;
        if (!SUPPORTED_MODELS.has(model)) {
            this.enabled = false;
            this.onOff.value = "0";
            this.updateUi();
            this.clear();
            this.setStatus("このモデルには新形式の等値線がありません。");
            return;
        }
        const { ini } = this.initialTime.getParams();
        if (!(await this.initialTime.hasLatest(`${model}P`, ini))) {
            this.enabled = false;
            this.onOff.value = "0";
            this.updateUi();
            this.clear();
            this.setStatus("この初期値の等値線データは作成中です。");
            return;
        }
        this.setStatus("");
        const isQma = model === "QMA";
        // QMA の pmtiles はバックフィル起点 (dir=20260711-00) 以降のみ。
        // それより古い対象時刻は従来 SVG へ直行 (無駄な 404 プローブを避ける)
        if (
            isQma &&
            this.qmaKey().directory.format("YYYYMMDD-HH") <
                QMA_CONTOUR_PMTILES_DIRECTORY_CUTOFF
        ) {
            await this.loadLegacy(
                this.qmaUrl("svg"),
                currentSequence,
                resetStyle,
            );
            return;
        }
        if (
            !isQma &&
            model !== "GSA" &&
            model !== "GSL" &&
            beforeInitialCutoff(ini, CONTOUR_PMTILES_CUTOFF)
        ) {
            await this.loadLegacy(
                this.makeLegacyUrl(),
                currentSequence,
                resetStyle,
            );
            return;
        }
        const url = isQma ? this.makeQmaUrl() : this.makeUrl();
        const scope = `${model}|${ini.format("YYYYMMDD-HH")}|${this.version.value}`;
        if (url === this.archiveUrl && this.archive) {
            if (resetStyle || this.styleNeedsInitialization) {
                this.initializeStyle();
            }
            await this.draw();
            return;
        }
        const archive = await this.getArchive(url);
        if (currentSequence !== this.sequence) {
            return;
        }
        if (!archive) {
            if (model === "GSA" || model === "GSL") {
                this.clear();
                throw new Error(`contour PMTiles not found: ${url}`);
            }
            if (isQma) {
                // 取得失敗時は従来 SVG にフォールバック (per-FT なので安全)
                await this.loadLegacy(
                    this.qmaUrl("svg"),
                    currentSequence,
                    resetStyle,
                );
                return;
            }
            await this.loadLegacy(
                this.makeLegacyUrl(),
                currentSequence,
                resetStyle,
            );
            return;
        }
        const metadata = (await archive.getMetadata()) as ContourMetadata;
        if (currentSequence !== this.sequence) {
            return;
        }
        if (metadata.format !== "gpv-contour-v1" || !metadata.values?.length) {
            this.clear();
            throw new Error("unsupported contour PMTiles metadata");
        }
        if (model === "GSA" || model === "GSL") {
            const meta = getMapMeta(model);
            if (
                metadata.origin?.[0] !== meta.x0 ||
                metadata.origin?.[1] !== meta.y0 ||
                metadata.full_svg_units?.[0] !== meta.width ||
                metadata.full_svg_units?.[1] !== meta.height
            ) {
                this.clear();
                throw new Error(`${model} contour metadata range mismatch`);
            }
        }
        const scopeChanged = scope !== this.scope;
        this.archive = archive;
        this.legacyPaths = null;
        this.archiveUrl = url;
        this.scope = scope;
        this.values = [...metadata.values].map(Number).sort((a, b) => a - b);
        this.valueStep = Number(metadata.value_step) || this.defaultStep();
        this.buildOptions();
        if (scopeChanged || resetStyle || this.styleNeedsInitialization) {
            this.initializeStyle();
        }
        await this.draw();
        if (!isQma) {
            this.prefetch(); // prefetch の URL 計算は FT オフセット式で QMA 非対応
        }
    }
    private async draw() {
        const currentSequence = ++this.drawSequence;
        if (
            !this.enabled ||
            (!this.archive && !this.legacyPaths) ||
            this.selectedLevels.length === 0
        ) {
            this.clearCanvas();
            return;
        }
        const url = this.archiveUrl;
        const specs = this.layerSpecs();
        const pathGroups = await Promise.all(
            specs.map((spec) => this.getPaths(spec.value, this.archive, url)),
        );
        if (currentSequence !== this.drawSequence || url !== this.archiveUrl) {
            return;
        }
        const view = this.mapView.getViewBox();
        const rect = document
            .querySelector("#svg_main")!
            .getBoundingClientRect();
        const targetWidth = this.captureWidth ?? rect.width * devicePixelRatio;
        const width = Math.min(4096, Math.max(1, Math.round(targetWidth)));
        const scale = width / view.width;
        const height = Math.max(1, Math.round(view.height * scale));
        this.foreignObject.setAttribute("x", String(view.x));
        this.foreignObject.setAttribute("y", String(view.y));
        this.foreignObject.setAttribute("width", String(view.width));
        this.foreignObject.setAttribute("height", String(view.height));
        this.canvas.style.width = `${view.width}px`;
        this.canvas.style.height = `${view.height}px`;
        {
            // Chromium の foreignObject 再レイアウト対策 (CanvasTiles と同じ)
            const parent = this.foreignObject.parentNode!;
            const next = this.foreignObject.nextSibling;
            parent.removeChild(this.foreignObject);
            parent.insertBefore(this.foreignObject, next);
        }
        this.canvas.width = width;
        this.canvas.height = height;
        const context = this.canvas.getContext("2d")!;
        context.setTransform(
            scale,
            0,
            0,
            scale,
            -view.x * scale,
            -view.y * scale,
        );
        context.lineJoin = "round";
        context.lineCap = "round";
        const layers: Layer[] = [];
        specs.forEach((spec, index) => {
            const path = new Path2D();
            pathGroups[index].forEach((part) => path.addPath(part));
            const region = this.regionForValue(
                spec.value,
                path,
                pathGroups[index].length > 0,
                spec.side,
            );
            layers.push({
                value: spec.value,
                path,
                region,
                style: this.styleFor(spec.value, spec.indices),
            });
        });
        for (const layer of layers) {
            context.save();
            context.clip(layer.region, "evenodd");
            context.globalCompositeOperation = "copy";
            context.fillStyle = layer.style.fill.css;
            context.globalAlpha = layer.style.fill.alpha;
            context.fillRect(view.x, view.y, view.width, view.height);
            context.restore();
        }
        context.globalCompositeOperation = "source-over";
        for (const layer of layers) {
            context.strokeStyle = layer.style.line.css;
            context.globalAlpha = layer.style.line.alpha;
            context.lineWidth = this.strokeWidth.value * (view.width / 1920);
            context.stroke(layer.path);
        }
        context.globalAlpha = 1;
        this.hitLayers = layers;
        this.foreignObject.style.display = "";
        this.drawLegend();
    }

    private layerSpecs(): LayerSpec[] {
        const make = (indices: number[], side: "C" | "W") =>
            indices
                .filter((index) => this.thresholds[index] !== -99999)
                .sort((a, b) =>
                    side === "W"
                        ? this.thresholds[a] - this.thresholds[b]
                        : this.thresholds[b] - this.thresholds[a],
                )
                .map((index) => ({
                    value: this.thresholds[index],
                    side,
                    indices,
                }));
        if (this.side.value === "B") {
            return [...make([0, 1, 2], "C"), ...make([3, 4, 5], "W")];
        }
        return make([0, 1, 2, 3, 4, 5], this.side.value as "C" | "W");
    }

    private regionForValue(
        value: number,
        path: Path2D,
        hasPath: boolean,
        side: "C" | "W",
    ) {
        if (hasPath) {
            if (side === "C") {
                return path;
            }
            const warmRegion = this.fullMapPath();
            warmRegion.addPath(path);
            return warmRegion;
        }

        // The legacy renderer synthesized boundaries outside the data range.
        // These are required by the combined cold/warm preset.
        const minimum = this.values[0];
        const maximum = this.values.at(-1)!;
        const coversAll =
            (side === "C" && value > maximum) ||
            (side === "W" && value < minimum);
        return coversAll ? this.fullMapPath() : new Path2D();
    }
    private fullMapPath() {
        const meta = getMapMeta(this.shadeSelector.getParams().model);
        const path = new Path2D();
        path.rect(meta.x0, meta.y0, meta.width, meta.height);
        return path;
    }

    private async getPaths(
        value: number,
        archive: PMTiles | null,
        url: string,
    ) {
        if (this.legacyPaths) {
            return this.legacyPaths.get(value) ?? [];
        }
        if (!archive) {
            return [];
        }
        const key = `${url}|${value}`;
        const cached = this.paths.get(key);
        if (cached) {
            return cached;
        }
        const promise = (async () => {
            const model = this.shadeSelector.getParams().model;
            let x: number;
            if (model === "GSL") {
                const metadata =
                    (await archive.getMetadata()) as ContourMetadata;
                x = metadata.values?.map(Number).indexOf(value) ?? -1;
            } else {
                x = this.version.value === "E850" ? value / 5 + 64 : value + 64;
            }
            if (!Number.isInteger(x) || x < 0 || x >= 256) {
                return [];
            }
            const result = await archive.getZxy(TILE_Z, x, 0);
            if (!result) {
                return [];
            }
            return decodeContourPaths(await inflate(result.data));
        })();
        this.paths.set(key, promise);
        this.pathOrder.push(key);
        // タイムバー全体 × 選択レベル数が収まるだけ保持 (2026-08-02 拡大)
        while (this.pathOrder.length > 512) {
            this.paths.delete(this.pathOrder.shift()!);
        }
        try {
            return await promise;
        } catch (error) {
            this.paths.delete(key);
            throw error;
        }
    }
    private getArchive(url: string) {
        const cached = this.archives.get(url);
        if (cached) {
            return cached;
        }
        const promise = (async () => {
            try {
                const archive = new PMTiles(url);
                await archive.getHeader();
                return archive;
            } catch {
                this.archives.delete(url);
                return null;
            }
        })();
        this.archives.set(url, promise);
        this.archiveOrder.push(url);
        // タイムバー全体 (最大89本) が収まるだけ保持 (2026-08-02 拡大)
        while (this.archiveOrder.length > 128) {
            this.archives.delete(this.archiveOrder.shift()!);
        }
        return promise;
    }
    private makeUrl(ftOffset = 0) {
        const model = this.shadeSelector.getParams().model;
        const { ini } = this.initialTime.getParams();
        const { ft } = this.forecastTime.getParams();
        const actual = ft.add(ftOffset);
        if (model === "GSL" && usesGslAnalysis(actual)) {
            const { target, key } = gslTargetKey(ini, actual);
            return dataPath(
                `GSLP/${target.format("YYYYMM")}/analysis/GSL-${this.version.value}-A_${key}.pmtiles`,
            );
        }
        const width = model === "GSA" || model === "GSL" ? 5 : 4;
        const actualFt =
            model === "GSL"
                ? gslForecastKey(actual)
                : actual.toFixed(1).padStart(width, "0");
        const initial = ini.format("YYYYMMDD-HH");
        return dataPath(
            `${model}P/${ini.format("YYYYMM")}/${initial}/${model}-${this.version.value}_${initial}_FT${actualFt}.pmtiles`,
        );
    }
    private makeLegacyUrl() {
        return this.makeUrl().replace(/\.pmtiles$/, ".svg");
    }
    // QMA の対象時刻キー (ディレクトリ=切り上げ時・ファイル=切り捨て時+deci)
    private qmaKey() {
        const { ini } = this.initialTime.getParams();
        const { ft } = this.forecastTime.getParams();
        const integer = ft.floor();
        const deci = ft.sub(integer).mul(10).round().toNumber();
        const directory = ini.add(
            integer.toNumber() + Math.ceil(deci / 10),
            "hour",
        );
        const target = ini.add(integer.toNumber(), "hour");
        return { directory, target, deci };
    }
    private qmaUrl(extension: "pmtiles" | "svg") {
        const { directory, target, deci } = this.qmaKey();
        return dataPath(
            `QMAP/${directory.format("YYYYMM")}/${directory.format("YYYYMMDD-HH")}/QMA-${this.version.value}_${target.format("YYYYMMDD-HH")}.${deci}.${extension}`,
        );
    }
    private makeQmaUrl() {
        return this.qmaUrl("pmtiles");
    }
    private getLegacyPaths(url: string) {
        const cached = this.legacyCache.get(url);
        if (cached) {
            return cached;
        }
        const promise = (async () => {
            const response = await fetch(url);
            if (!response.ok) {
                return null;
            }
            const source = new DOMParser().parseFromString(
                await response.text(),
                "image/svg+xml",
            );
            if (source.querySelector("parsererror")) {
                return null;
            }
            const paths = new Map<number, Path2D[]>();
            // 配信中の旧MSM/GSM/QMA SVGは値をIDへ直接持つ
            // (例: _T-6, _E330)。同じ値の複数pathも保持する。
            for (const node of source.querySelectorAll("path[id]")) {
                const match = (node.getAttribute("id") || "").match(
                    /_[TE](-?\d+(?:\.\d+)?)$/,
                );
                const data = node.getAttribute("d");
                if (!match || !data) {
                    continue;
                }
                try {
                    const value = Number(match[1]);
                    const parts = paths.get(value) ?? [];
                    parts.push(new Path2D(data));
                    paths.set(value, parts);
                } catch {
                    // 壊れた1pathだけを除外し、他の等値線は表示する。
                }
            }
            return paths.size ? paths : null;
        })();
        promise.catch(() => this.legacyCache.delete(url));
        this.legacyCache.set(url, promise);
        this.legacyOrder.push(url);
        while (this.legacyOrder.length > 8) {
            this.legacyCache.delete(this.legacyOrder.shift()!);
        }
        return promise;
    }
    private async loadLegacy(
        url: string,
        currentSequence: number,
        resetStyle: boolean,
    ) {
        const { ini } = this.initialTime.getParams();
        if (url === this.archiveUrl && this.legacyPaths) {
            await this.draw();
            return;
        }
        const paths = await this.getLegacyPaths(url);
        if (!paths) {
            this.clear();
            throw new Error(`contour SVG not found: ${url}`);
        }
        if (currentSequence !== this.sequence) {
            return;
        }
        const model = this.shadeSelector.getParams().model;
        const scope = `${model}|${ini.format("YYYYMMDD-HH")}|${this.version.value}`;
        const scopeChanged = scope !== this.scope;
        this.archive = null;
        this.legacyPaths = paths;
        this.archiveUrl = url;
        this.scope = scope;
        this.values = [...paths.keys()].sort((a, b) => a - b);
        this.valueStep = this.defaultStep();
        this.buildOptions();
        if (scopeChanged || resetStyle) {
            this.initializeStyle();
        }
        await this.draw();
    }
    // 近傍 (前方12・後方6) の pmtiles を先読みする (ヘッダ + 表示中レベルのパス)。
    // オフセットは FT 刻み幅単位 (GS5 3h 等でも実在する FT を指す)。
    // 進行方向側を先に取得する (画像系 Shade.preloadCanvas と同じ規則。2026-08-02)
    private prefetch() {
        const { ft } = this.forecastTime.getParams();
        const direction =
            this.prefetchPreviousFt === null ||
            ft.gte(this.prefetchPreviousFt)
                ? (1 as const)
                : (-1 as const);
        this.prefetchPreviousFt = ft;
        const specs = this.layerSpecs();
        for (const neighbor of this.forecastTime.getNeighborFts(
            ft,
            direction,
        )) {
            const url = this.makeUrl(neighbor.sub(ft).toNumber());
            void this.getArchive(url).then((archive) => {
                if (!archive) {
                    return;
                }
                for (const spec of specs) {
                    void this.getPaths(spec.value, archive, url).catch(
                        () => undefined,
                    );
                }
            });
        }
    }
    private buildOptions() {
        const step = this.step();
        const options = [
            ...new Set([
                this.values[0] - step,
                ...this.values,
                this.values.at(-1)! + step,
                ...this.thresholds.filter((value) => value !== -99999),
                ...(this.baseVersion() === "T850"
                    ? [-18, -12, -6, 0, 6, 12, 18, 24, 30]
                    : []),
                ...(this.baseVersion() === "T500"
                    ? [-42, -36, -30, -24, -12, -6]
                    : []),
            ]),
        ].sort((a, b) => a - b);
        this.thresholdSelects.forEach((threshold) => {
            threshold.textContent = "";
            threshold.appendChild(new Option("--", "-99999"));
            options.forEach((value) =>
                threshold.appendChild(new Option(String(value), String(value))),
            );
        });
        this.updateThresholdUi();
        this.updatePresetLinks();
    }
    private initializeStyle() {
        if (this.paramsExplicitStyle) {
            this.updateSelectedLevels();
            this.updateThresholdUi();
        } else {
            this.applyPreset(this.defaultPresetName(), false);
        }
        this.paramsExplicitStyle = false;
        this.styleNeedsInitialization = false;
    }
    private applyPreset(name: string, redraw = true) {
        const preset = this.preset(name);
        if (!preset) {
            return;
        }
        preset.fills.forEach((color, index) =>
            this.fillColors[index].setColor(color),
        );
        (preset.lines ?? DEFAULT_LINES).forEach((color, index) =>
            this.lineColors[index].setColor(color),
        );
        for (let index = 0; index < 6; index++) {
            this.thresholds[index] = preset.thresholds[index] ?? -99999;
        }
        this.side.value = preset.side;
        this.updateSelectedLevels();
        this.updateThresholdUi();
        if (redraw) {
            this.changed();
        }
    }
    private preset(name: string): Preset | null {
        const presets: Record<string, Preset> = {
            "T-6": {
                fills: ["#000066cc", "#2255eecc", "#5555ff77"],
                thresholds: [-18, -12, -6],
                side: "C",
            },
            T0: {
                fills: ["#000066cc", "#2255eecc", "#5555ff77", "#ccccff77"],
                thresholds: [-18, -12, -6, 0],
                side: "C",
            },
            "T-6S": {
                fills: ["#5555ff77", "#ccccff77"],
                thresholds: [-12, -6],
                side: "C",
            },
            T3: {
                fills: [
                    "#2255eecc",
                    "#ffffff00",
                    "#ffffff00",
                    "#ffffff00",
                    "#ffffff00",
                    "#ff9933cc",
                ],
                thresholds: [-6, -99999, -99999, -99999, -99999, 6],
                side: "B",
            },
            T6: {
                fills: ["#ee2266cc", "#ff6633cc", "#ff9933cc", "#ffcc88cc"],
                thresholds: [24, 18, 12, 6],
                side: "W",
            },
            T12: {
                fills: ["#ee2266cc", "#ff6633cc", "#ff9933cc", "#ffcc88cc"],
                thresholds: [30, 24, 18, 12],
                side: "W",
            },
            "T-30": {
                fills: ["#000066cc", "#2255eecc", "#5555ff77"],
                thresholds: [-42, -36, -30],
                side: "C",
            },
            "T-24": {
                fills: ["#000066cc", "#2255eecc", "#5555ff77", "#ccccff77"],
                thresholds: [-42, -36, -30, -24],
                side: "C",
            },
            H5880: {
                fills: ["#eb2365e6", "#f86e44d9"],
                thresholds: [5940, 5880],
                side: "W",
            },
            H500_LINE: {
                fills: ["#ffffff00"],
                lines: ["#3333ffff"],
                thresholds: [4260],
                side: "C",
            },
            H9720: {
                fills: ["#fc9e0acc"],
                thresholds: [9720],
                side: "W",
            },
            H300_LINE: {
                fills: ["#ffffff00"],
                lines: ["#3333ffff"],
                thresholds: [6960],
                side: "C",
            },
            E330: {
                fills: ["#eb2365e6", "#f86e44d9", "#fc9e0acc"],
                thresholds: [360, 345, 330],
                side: "W",
            },
            E345: {
                fills: ["#eb2365e6", "#f86e44d9", "#fc9e0acc", "#edc41fcc"],
                thresholds: [360, 355, 350, 345],
                side: "W",
            },
        };
        return presets[name] ?? null;
    }
    private styleFor(value: number, candidateIndices = [0, 1, 2, 3, 4, 5]) {
        const indices = this.activeThresholdIndices(candidateIndices);
        if (!indices.length) {
            return {
                fill: parseRgba("#ffffff00"),
                line: parseRgba("#ffffffff"),
            };
        }
        const ordered = [...indices].sort(
            (a, b) => this.thresholds[a] - this.thresholds[b],
        );
        const first = ordered[0];
        if (value <= this.thresholds[first]) {
            return {
                fill: parseRgba(this.fillColors[first].getColor()),
                line: parseRgba(this.lineColors[first].getColor()),
            };
        }
        for (let position = 1; position < ordered.length; position++) {
            const previous = ordered[position - 1];
            const next = ordered[position];
            if (value <= this.thresholds[next]) {
                const ratio =
                    (value - this.thresholds[previous]) /
                    (this.thresholds[next] - this.thresholds[previous]);
                return {
                    fill: interpolateRgba(
                        parseRgba(this.fillColors[previous].getColor()),
                        parseRgba(this.fillColors[next].getColor()),
                        ratio,
                    ),
                    line: interpolateRgba(
                        parseRgba(this.lineColors[previous].getColor()),
                        parseRgba(this.lineColors[next].getColor()),
                        ratio,
                    ),
                };
            }
        }
        if (value > this.thresholds[ordered.at(-1)!]) {
            return {
                fill: parseRgba("#ffffff00"),
                line: parseRgba(this.lineColors[ordered.at(-1)!].getColor()),
            };
        }
        return {
            fill: parseRgba("#ffffff00"),
            line: parseRgba("#ffffffff"),
        };
    }
    private activeThresholdIndices(candidateIndices = [0, 1, 2, 3, 4, 5]) {
        const result: number[] = [];
        for (const index of candidateIndices) {
            if (this.thresholds[index] !== -99999) {
                result.push(index);
            }
        }
        return result;
    }
    private updatePresetLinks() {
        const names: Record<string, [string, string][]> = {
            T975: [
                ["T0", "0℃"],
                ["T-6", "-6℃"],
            ],
            T925: [
                ["T0", "0℃"],
                ["T-6", "-6℃"],
            ],
            T850: [
                ["T0", "寒0℃"],
                ["T-6", "寒-6℃"],
                ["T6", "暖6℃"],
                ["T12", "暖12℃"],
                ["T3", "寒/暖"],
            ],
            T500: [
                ["T-30", "-30℃"],
                ["T-24", "-24℃"],
                ["T-6S", "-6℃"],
            ],
            E850: [
                ["E330", "330K"],
                ["E345", "345K"],
            ],
            H500: [
                ["H5880", "5880m以上"],
                ["H500_LINE", "線のみ"],
            ],
            H300: [
                ["H9720", "9720m以上"],
                ["H300_LINE", "線のみ"],
            ],
            T300: [["T-30", "-30℃"]],
        };
        for (let index = 0; index < 5; index++) {
            const link = document.querySelector<HTMLAnchorElement>(
                `#Cpreset${index + 4}`,
            )!;
            const item = names[this.baseVersion()]?.[index];
            link.textContent = item?.[1] ?? "";
            link.dataset.preset = item?.[0] ?? "";
            link.href = "#";
        }
    }
    private defaultPresetName() {
        return (
            {
                T975: "T0",
                T925: "T0",
                T850: "T0",
                T500: "T-30",
                E850: "E330",
                H500: "H5880",
                H300: "H9720",
                T300: "T-30",
            } as Record<string, string>
        )[this.baseVersion()];
    }
    private updateThresholdUi() {
        this.thresholdSelects.forEach((select, index) => {
            select.value = String(this.thresholds[index]);
        });
    }
    private updateModelOptions() {
        const model = this.shadeSelector.getParams().model;
        const versions: ContourVersion[] = [
            "T975",
            "T925",
            "T850",
            "E850",
            "T500",
            "H500",
            "H300",
            "T300",
            "T850A",
            "H500A",
            "T500A",
            "H300A",
            "T300A",
        ];
        const allowed: Partial<Record<string, ContourVersion[]>> = {
            MSM: ["T975", "T925", "T850", "E850"],
            QMA: ["T975", "T925", "T850", "E850"],
            GSM: ["T925", "T850", "E850", "T500"],
            GSA: ["T925", "T850", "E850", "T500"],
            GSL: [
                "T850A",
                "H500A",
                "T500A",
                "H300A",
                "T300A",
                "T850",
                "H500",
                "T500",
                "H300",
                "T300",
                "E850",
            ],
        };
        const choices = allowed[model] ?? [];
        for (const version of versions) {
            $(`#op_Cver_${version}`).prop(
                "disabled",
                !choices.includes(version),
            );
        }
        if (!choices.includes(this.version.value as ContourVersion)) {
            this.version.value = choices[0] ?? "T850";
        }
        // GSA はデータ有効域セクター、GSL は生成範囲の矩形でクリップする。
        // 既存モデルは GPV.svg 内の cliparea_GSM/MSM (従来どおり)。
        const clipModel =
            model === "GSA" || model === "GSL"
                ? model
                : model === "GSM"
                  ? "GSM"
                  : "MSM";
        this.group.setAttribute(
            "clip-path",
            `url(#cliparea_${clipModel})`,
        );
    }
    private updateUi() {
        this.updateAvailability();
        const display = this.enabled ? "block" : "none";
        $("#sdm_contour_detail").css("display", display);
        $("#btn_contour_on")
            .toggleClass("btn_onA", this.enabled)
            .toggleClass("btn_onB", !this.enabled);
        $("#btn_contour_off")
            .toggleClass("btn_offB", this.enabled)
            .toggleClass("btn_offA", !this.enabled);
        $(".contour-style-grid").toggleClass(
            "contour-style-grid--combined",
            this.side.value === "B",
        );
        this.updatePresetLinks();
        this.updateThresholdUi();
        if (!this.enabled) {
            this.legend.textContent = "";
        }
    }
    private updateAvailability() {
        setFeatureAvailability(
            "contour",
            SUPPORTED_MODELS.has(this.shadeSelector.getParams().model),
        );
    }
    private drawLegend() {
        this.legend.textContent = "";
        if (!this.enabled) {
            return;
        }
        const labels: Record<string, [string, string, string]> = {
            T975: ["気温", "℃", "約300m"],
            T925: ["気温", "℃", "約750m"],
            T850: ["気温", "℃", "約1500m"],
            T500: ["気温", "℃", "約5500m"],
            T300: ["気温", "℃", "約9500m"],
            E850: ["暖湿流", "K", "約1500m"],
            H500: ["高度", "m", "500hPa"],
            H300: ["高度", "m", "300hPa"],
            T850A: ["気温", "℃", "850hPa 平年偏差"],
            H500A: ["高度", "m", "500hPa 平年偏差"],
            T500A: ["気温", "℃", "500hPa 平年偏差"],
            H300A: ["高度", "m", "300hPa 平年偏差"],
            T300A: ["気温", "℃", "300hPa 平年偏差"],
        };
        const [title, unit, altitude] = labels[this.version.value];
        const coldIndices = this.activeThresholdIndices([0, 1, 2]).sort(
            (a, b) => this.thresholds[a] - this.thresholds[b],
        );
        const warmIndices = this.activeThresholdIndices([3, 4, 5]).sort(
            (a, b) => this.thresholds[b] - this.thresholds[a],
        );
        const hasCombinedGap =
            this.side.value === "B" &&
            coldIndices.length > 0 &&
            warmIndices.length > 0;
        const entries =
            this.side.value === "B"
                ? [
                      ...coldIndices.map((colorIndex, row) => ({
                          colorIndex,
                          row,
                          numberAtTop: false,
                      })),
                      ...warmIndices.map((colorIndex, index) => ({
                          colorIndex,
                          row:
                              coldIndices.length +
                              (hasCombinedGap ? 1 : 0) +
                              index,
                          numberAtTop: true,
                      })),
                  ]
                : this.activeThresholdIndices()
                      .sort((a, b) =>
                          this.side.value === "W"
                              ? this.thresholds[b] - this.thresholds[a]
                              : this.thresholds[a] - this.thresholds[b],
                      )
                      .map((colorIndex, row) => ({
                          colorIndex,
                          row,
                          numberAtTop: false,
                      }));
        const margin = 13;
        const titleHeight = 95;
        const cellWidth = 50;
        const cellHeight = 48;
        const width = 170;
        const rowCount = entries.length + (hasCombinedGap ? 1 : 0);
        const height = margin * 2 + titleHeight + rowCount * cellHeight + 10;
        this.legend.setAttribute(
            "transform",
            `translate(${this.legendX} ${this.legendY})`,
        );
        this.legend.appendChild(
            svgElement("rect", { width, height, fill: "#efe", rx: 12 }),
        );
        this.legend.appendChild(svgText(width / 2, margin + 42, title, 50));
        this.legend.appendChild(svgText(width / 2, margin + 80, altitude, 30));
        entries.forEach(({ colorIndex, row, numberAtTop }) => {
            const color = parseRgba(this.fillColors[colorIndex].getColor());
            this.legend.appendChild(
                svgElement("rect", {
                    x: margin,
                    y: margin + titleHeight + row * cellHeight,
                    width: cellWidth,
                    height: cellHeight,
                    fill: color.css,
                    opacity: color.alpha,
                }),
            );
            this.legend.appendChild(
                svgText(
                    cellWidth + margin + (width - cellWidth - margin) / 2,
                    margin +
                        titleHeight +
                        (numberAtTop ? row : row + 1) * cellHeight +
                        35 * 0.35,
                    `${this.thresholds[colorIndex]}${unit}`,
                    35,
                ),
            );
        });
    }
    private installLegendDrag() {
        this.legend.addEventListener("mousedown", (start) => {
            if (start.button !== 0) {
                return;
            }
            start.preventDefault();
            const originX = this.legendX;
            const originY = this.legendY;
            const moved = (event: MouseEvent) => {
                const rect = document
                    .querySelector("#overlay")!
                    .getBoundingClientRect();
                this.legendX =
                    originX +
                    ((event.clientX - start.clientX) * 1920) / rect.width;
                this.legendY =
                    originY +
                    ((event.clientY - start.clientY) * 1080) / rect.height;
                this.legend.setAttribute(
                    "transform",
                    `translate(${this.legendX} ${this.legendY})`,
                );
            };
            const ended = () => {
                window.removeEventListener("mousemove", moved);
                window.removeEventListener("mouseup", ended);
            };
            window.addEventListener("mousemove", moved);
            window.addEventListener("mouseup", ended);
        });
    }
    private hover(event: MouseEvent) {
        if (!this.enabled || !this.hitLayers.length) {
            return;
        }
        const rect = this.canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) {
            return;
        }
        const x =
            ((event.clientX - rect.left) * this.canvas.width) / rect.width;
        const y =
            ((event.clientY - rect.top) * this.canvas.height) / rect.height;
        const context = this.canvas.getContext("2d")!;
        context.save();
        context.lineWidth = 3 * (this.mapView.getViewBox().width / 1920);
        let hit: number | null = null;
        for (let index = this.hitLayers.length - 1; index >= 0; index--) {
            if (context.isPointInStroke(this.hitLayers[index].path, x, y)) {
                hit = this.hitLayers[index].value;
                break;
            }
        }
        context.restore();
        if (hit === null) {
            $("#info").hide();
        } else {
            const unit = this.version.value.startsWith("E")
                ? "K"
                : this.version.value.startsWith("H")
                  ? "m"
                  : "℃";
            $("#info")
                .text(`${hit}${unit}`)
                .css({ top: `${event.pageY - 15}px`, left: `${event.pageX}px` })
                .show();
        }
    }
    private clear() {
        ++this.drawSequence;
        this.archive = null;
        this.legacyPaths = null;
        this.archiveUrl = "";
        this.hitLayers = [];
        this.legend.textContent = "";
        this.clearCanvas();
        this.foreignObject.style.display = "none";
        $("#info").hide();
    }
    private clearCanvas() {
        this.canvas
            .getContext("2d")
            ?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    private setStatus(message: string) {
        $("#contour_status").text(message);
    }
    private step() {
        return this.valueStep || this.defaultStep();
    }
    private defaultStep() {
        const steps: Partial<Record<ContourVersion, number>> = {
            E850: 5,
            H500: 60,
            H500A: 60,
            H300: 120,
            H300A: 120,
            T850: 3,
            T850A: 3,
            T500: 3,
            T500A: 3,
            T300: 3,
            T300A: 3,
        };
        return steps[this.version.value as ContourVersion] ?? 1;
    }
    private baseVersion() {
        return this.version.value.replace(/A$/, "");
    }
    private updateSelectedLevels() {
        this.selectedLevels = [
            ...new Set(
                this.thresholds.filter(
                    (value) => Number.isFinite(value) && value !== -99999,
                ),
            ),
        ];
    }
    private validVersion(value: string | null): ContourVersion {
        return (
            [
                "T975",
                "T925",
                "T850",
                "E850",
                "T500",
                "H500",
                "H300",
                "T300",
                "T850A",
                "H500A",
                "T500A",
                "H300A",
                "T300A",
            ].includes(value || "")
                ? value
                : "T850"
        ) as ContourVersion;
    }
    private numberParam(
        key: string,
        fallback: number,
        minimum: number,
        maximum: number,
    ) {
        if (!this.params.has(key)) {
            return fallback;
        }
        const value = Number(this.params.get(key));
        return Number.isFinite(value)
            ? Math.min(maximum, Math.max(minimum, value))
            : fallback;
    }
    private loadColors(key: string, pickers: ColorPicker[]) {
        const colors = this.params.get(key)?.split(",");
        if (
            colors?.length === 6 &&
            colors.every((color) => /^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(color))
        ) {
            colors.forEach((color, index) => pickers[index].setColor(color));
        }
    }
}

async function inflate(bytes: ArrayBuffer | Uint8Array) {
    const stream = new Blob([bytes])
        .stream()
        .pipeThrough(new DecompressionStream("deflate"));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

export function decodeContourPaths(bytes: Uint8Array) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (bytes.byteLength < 4 || view.getUint8(0) !== 1) {
        throw new Error("unsupported contour payload");
    }
    const pathCount = view.getUint16(2, true);
    let offset = 4;
    const paths: Path2D[] = [];
    for (let index = 0; index < pathCount; index++) {
        if (offset + 10 > bytes.byteLength) {
            throw new Error("truncated contour path");
        }
        const segmentCount = view.getUint16(offset, true);
        let x = view.getInt32(offset + 2, true);
        let y = view.getInt32(offset + 6, true);
        offset += 10;
        const path = new Path2D();
        path.moveTo(x / 2, y / 2);
        for (let segment = 0; segment < segmentCount; segment++) {
            if (offset + 12 > bytes.byteLength) {
                throw new Error("truncated contour segment");
            }
            const c1x = view.getInt16(offset, true);
            const c1y = view.getInt16(offset + 2, true);
            const c2x = view.getInt16(offset + 4, true);
            const c2y = view.getInt16(offset + 6, true);
            const endX = view.getInt16(offset + 8, true);
            const endY = view.getInt16(offset + 10, true);
            offset += 12;
            path.bezierCurveTo(
                (x + c1x) / 2,
                (y + c1y) / 2,
                (x + c2x) / 2,
                (y + c2y) / 2,
                (x + endX) / 2,
                (y + endY) / 2,
            );
            x += endX;
            y += endY;
        }
        path.closePath();
        paths.push(path);
    }
    if (offset !== bytes.byteLength) {
        throw new Error("unexpected contour payload tail");
    }
    return paths;
}

function parseRgba(hex: string): Rgba {
    const normalized = hex.toLowerCase();
    if (/^#[0-9a-f]{8}$/.test(normalized)) {
        return {
            css: normalized.slice(0, 7),
            alpha: Number.parseInt(normalized.slice(7, 9), 16) / 255,
        };
    }
    return { css: normalized, alpha: 1 };
}

function interpolateRgba(from: Rgba, to: Rgba, ratio: number): Rgba {
    const channels = (color: string) =>
        [1, 3, 5].map((offset) =>
            Number.parseInt(color.slice(offset, offset + 2), 16),
        );
    const start = channels(from.css);
    const end = channels(to.css);
    const values = start.map((value, index) =>
        Math.round(value + (end[index] - value) * ratio),
    );
    return {
        css: `#${values.map((value) => value.toString(16).padStart(2, "0")).join("")}`,
        alpha: from.alpha + (to.alpha - from.alpha) * ratio,
    };
}

function svgElement(name: string, attributes: Record<string, string | number>) {
    const element = document.createElementNS(SVG_NS, name);
    for (const [key, value] of Object.entries(attributes)) {
        element.setAttribute(key, String(value));
    }
    return element;
}

function svgText(x: number, y: number, content: string, fontSize: number) {
    const element = svgElement("text", {
        x,
        y,
        "font-size": fontSize,
        fill: "#000",
        "text-anchor": "middle",
    });
    element.textContent = content;
    return element;
}
